import { CognitoUser } from 'amazon-cognito-identity-js';
import { Auth } from 'aws-amplify';
import sealedbox from 'tweetnacl-sealedbox-js';

import { CURRENT_ENV } from '@/shared/environments';

// Публичный ключ для шифрования
const SCALE_ADS_PUBLIC_KEY = 'HQWLMB6WXYQcFQxp4eWcrNWxpwOZNAqNI6AeGCJrQQk=';

/**
 * Конвертирует Uint8Array в base64url строку
 * @param u8 - массив байтов для конвертации
 * @returns base64url строка
 */
const b64url = (u8: Uint8Array): string => {
  // Конвертируем Uint8Array в строку base64
  const base64 = btoa(String.fromCharCode.apply(null, Array.from(u8)));
  // Конвертируем base64 в base64url
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

/**
 * Конвертирует base64 строку в Uint8Array
 * @param base64 - строка в формате base64
 * @returns массив байтов
 */
const base64ToUint8Array = (base64: string): Uint8Array => {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.codePointAt(i)!;
  }
  return bytes;
};

/**
 * Шифрует данные с использованием публичного ключа получателя
 * @param plaintext - текст для шифрования
 * @param recipientPublicKeyB64 - публичный ключ получателя в base64
 * @returns зашифрованная строка в формате base64url
 */
function encodeSecret25519(plaintext: string, recipientPublicKeyB64: string): string {
  const pub = base64ToUint8Array(recipientPublicKeyB64); // 32 bytes
  const msg = new TextEncoder().encode(plaintext);
  const ct = sealedbox.seal(msg, pub);
  return b64url(ct); // base64url string
}

/**
 * Создает зашифрованный URL с пользовательскими данными
 * @param user - объект пользователя Cognito
 * @returns зашифрованная строка с данными пользователя
 */
const createEncryptedUserData = (user: CognitoUser): string => {
  const idToken = user.getSignInUserSession()?.getIdToken();
  const token = idToken?.getJwtToken() || '';

  const payload = {
    token,
    user: {
      name: idToken?.payload.name || '',
      picture: idToken?.payload.picture || '',
    },
  };

  const userDataString = JSON.stringify(payload);
  return encodeSecret25519(userDataString, SCALE_ADS_PUBLIC_KEY);
};

/**
 * Перенаправляет пользователя в Scale Ads с зашифрованными данными для SSO
 */
export const redirectToScaleAdsSSO = async (): Promise<void> => {
  try {
    const TARGET_DOMAIN = CURRENT_ENV ? `app.${CURRENT_ENV}.scale-ads.com` : 'app.scale-ads.com';
    
    const user = (await Auth.currentAuthenticatedUser()) as CognitoUser;
    if (user) {
      const encryptedUserData = createEncryptedUserData(user);
      const ssoUrl = `https://${TARGET_DOMAIN}/?code=${encryptedUserData}`;
      window.location.href = ssoUrl;
    } else {
      window.location.href = '/auth';
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('SSO redirect failed:', error);
    window.location.href = '/auth';
  }
};